import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import {
  EmbedSettingsView,
  Entity,
  getCollections,
  NEWS_SOURCE_SETTINGS_ID,
  NewsSourceType,
  NewsSyncStats,
  NewsSyncStatus,
  SaveEmbedAllowedDomainsRequest,
  TenantBillingInfo,
  TenantConfig,
  TenantNewsSourceSettings,
  TenantNotificationRecipients,
  TenantSeasonSettings,
  VerifactuTenantSettings,
} from '@models';
import { convertTimestampsToDates } from '@utils';
import { Observable, catchError, map, of } from 'rxjs';
import { FirestoreService } from './firestore.service';

const TENANTS_COLLECTION = 'tenants';
const SETTINGS_COLLECTION = 'settings';
const SEASON_SETTINGS_ID = 'season';
const BILLING_INFO_ID = 'billing_info';
const VERIFACTU_SETTINGS_ID = 'verifactu';
const NOTIFICATION_RECIPIENTS_ID = 'notification_recipients';
const MASTERS_SETTINGS_ID = 'settings';

export { NEWS_SOURCE_SETTINGS_ID };

/** Campos del origen de noticias que edita el admin (feature #118, R7). */
export type NewsSourceSettingsInput = Pick<
  TenantNewsSourceSettings,
  'mode' | 'type' | 'baseUrl' | 'syncEnabled'
> &
  Partial<Pick<TenantNewsSourceSettings, 'categoryIds' | 'language'>>;

/**
 * Resultado de la callable `syncTenantNewsNow` (contrato as-built de
 * `apps/functions/src/news-sync/sync-news.ts`). La promesa **resuelve** también
 * cuando el sync falla: en ese caso `status === 'error'` y `error` trae el
 * mensaje ya recortado a 500 caracteres.
 */
export interface NewsSourceSyncResult extends NewsSyncStats {
  status: NewsSyncStatus;
  error?: string;
}

/** Payload de la callable `testNewsSourceConnection`. */
export interface TestNewsSourceConnectionRequest {
  type: NewsSourceType;
  baseUrl: string;
  categoryIds?: number[];
  language?: string;
}

/** Causas de fallo que devuelve `testNewsSourceConnection`. */
export type NewsSourceConnectionErrorCode =
  | 'invalid-url'
  | 'unreachable'
  | 'not-wordpress'
  | 'rest-disabled'
  | 'http-error';

/** Resultado de la callable `testNewsSourceConnection`. */
export interface TestNewsSourceConnectionResult {
  ok: boolean;
  sampleTitle?: string;
  totalPosts?: number;
  errorCode?: NewsSourceConnectionErrorCode;
}

@Injectable({
  providedIn: 'root',
})
export class TenantsService {
  constructor(
    private readonly firestore: FirestoreService,
    private readonly firestoreDb: Firestore,
    private readonly functions: Functions,
  ) {}

  list(): Observable<TenantConfig[]> {
    return this.firestore.listDocuments<TenantConfig>(TENANTS_COLLECTION);
  }

  listFederations(): Observable<{ label: string; value: string }[]> {
    return this.list().pipe(
      map((tenants) =>
        tenants
          .filter((t) => t.type === 'federation')
          .map((t) => ({ label: t.name, value: t.id })),
      ),
    );
  }

  get(id: string): Observable<TenantConfig> {
    return this.firestore.getDocument<TenantConfig>(TENANTS_COLLECTION, id);
  }

  create(tenant: TenantConfig): Promise<string> {
    return this.firestore.create<TenantConfig>(
      TENANTS_COLLECTION,
      tenant,
      tenant.id,
    );
  }

  update(id: string, changes: Partial<TenantConfig>): Promise<void> {
    return this.firestore.update<TenantConfig>(TENANTS_COLLECTION, {
      id,
      ...changes,
    });
  }

  /**
   * Crea `tenants/{tenantId}/masters/settings` con los flags que la app lee al
   * arrancar (`SettingsMasterService`). Solo en el alta del tenant; mismo
   * contenido que escribe `createTenantTrial` en Cloud Functions.
   */
  createMastersSettings(tenantId: string): Promise<void> {
    return this.firestore.createOrUpdate<
      Entity & { maintenance: boolean; minVersion: string }
    >(getCollections(tenantId).MASTERS, MASTERS_SETTINGS_ID, {
      maintenance: false,
      minVersion: '0.0.0',
    });
  }

  generateId(): string {
    return this.firestore.generateId(TENANTS_COLLECTION);
  }

  inviteAdmin(
    tenantId: string,
    adminEmail: string,
  ): Promise<{ success: boolean; resetLink: string }> {
    const fn = httpsCallable<
      { tenantId: string; adminEmail: string },
      { success: boolean; resetLink: string }
    >(this.functions, 'inviteTenantAdmin');
    return fn({ tenantId, adminEmail }).then((res) => res.data);
  }

  getSeasonSettings(tenantId: string): Observable<TenantSeasonSettings | null> {
    return this.firestore
      .getDocument<TenantSeasonSettings>(
        `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
        SEASON_SETTINGS_ID,
      )
      .pipe(catchError(() => of(null)));
  }

  saveSeasonSettings(
    tenantId: string,
    seasonChangeMonth: number,
  ): Promise<void> {
    return this.firestore.createOrUpdate<TenantSeasonSettings>(
      `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
      SEASON_SETTINGS_ID,
      { seasonChangeMonth },
    );
  }

  getBillingInfo(tenantId: string): Observable<TenantBillingInfo | null> {
    return this.firestore
      .getDocument<TenantBillingInfo>(
        `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
        BILLING_INFO_ID,
      )
      .pipe(catchError(() => of(null)));
  }

  saveBillingInfo(
    tenantId: string,
    info: Omit<TenantBillingInfo, 'id'>,
  ): Promise<void> {
    return this.firestore.createOrUpdate<TenantBillingInfo>(
      `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
      BILLING_INFO_ID,
      info,
    );
  }

  getVerifactuSettings(
    tenantId: string,
  ): Observable<VerifactuTenantSettings | null> {
    // Usar docData directamente en lugar de FirestoreService.getDocument:
    // getDocument filtra 'undefined' (doc inexistente) haciendo que el
    // observable nunca emita cuando el documento no existe todavía.
    const docRef = doc(
      this.firestoreDb,
      `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}/${VERIFACTU_SETTINGS_ID}`,
    );
    return (
      docData(docRef, { idField: 'id' }) as Observable<
        VerifactuTenantSettings | undefined
      >
    ).pipe(
      map((data) => data ?? null),
      catchError(() => of(null)),
    );
  }

  saveVerifactuSettings(
    tenantId: string,
    settings: Omit<VerifactuTenantSettings, 'id'>,
  ): Promise<void> {
    return this.firestore.createOrUpdate<VerifactuTenantSettings>(
      `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
      VERIFACTU_SETTINGS_ID,
      settings,
    );
  }

  getNotificationRecipients(
    tenantId: string,
  ): Observable<TenantNotificationRecipients | null> {
    return this.firestore
      .getDocument<TenantNotificationRecipients>(
        `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
        NOTIFICATION_RECIPIENTS_ID,
      )
      .pipe(catchError(() => of(null)));
  }

  saveNotificationRecipients(
    tenantId: string,
    recipients: TenantNotificationRecipients['recipients'],
    updatedBy?: string,
  ): Promise<void> {
    return this.firestore.createOrUpdate<TenantNotificationRecipients>(
      `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
      NOTIFICATION_RECIPIENTS_ID,
      { recipients, ...(updatedBy ? { updatedBy } : {}) },
    );
  }

  /**
   * Configuración del origen de noticias del tenant (feature #118, R2).
   *
   * Usa `docData` directamente (patrón `getVerifactuSettings`) porque
   * `FirestoreService.getDocument` filtra `undefined` y nunca emitiría mientras
   * el documento no existe — aquí "no existe" es un estado normal (tenant que
   * todavía no ha configurado nada) y debe emitir `null`.
   * A diferencia de `getVerifactuSettings`, la referencia se construye con
   * `FirestoreService.getDocumentRef` para leer del **mismo** proyecto Firebase
   * en el que escribe `saveNewsSourceSettings` (los tenants enterprise tienen
   * proyecto propio).
   */
  getNewsSourceSettings(
    tenantId: string,
  ): Observable<TenantNewsSourceSettings | null> {
    const docRef = this.firestore.getDocumentRef<TenantNewsSourceSettings>(
      `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
      NEWS_SOURCE_SETTINGS_ID,
    );
    return (
      docData(docRef, { idField: 'id' }) as Observable<
        TenantNewsSourceSettings | undefined
      >
    ).pipe(
      // `lastSyncAt` / `updatedAt` llegan como Timestamp de Firestore.
      map((data) => (data ? convertTimestampsToDates(data) : null)),
      catchError(() => of(null)),
    );
  }

  /**
   * Guarda la configuración editable del origen de noticias (merge: los campos
   * de estado `lastSync*` que escribe la Cloud Function no se tocan).
   */
  saveNewsSourceSettings(
    tenantId: string,
    settings: NewsSourceSettingsInput,
    updatedBy?: string,
  ): Promise<void> {
    return this.firestore.createOrUpdate<TenantNewsSourceSettings>(
      `${TENANTS_COLLECTION}/${tenantId}/${SETTINGS_COLLECTION}`,
      NEWS_SOURCE_SETTINGS_ID,
      { ...settings, ...(updatedBy ? { updatedBy } : {}) },
    );
  }

  /**
   * Resync manual del tenant activo (R13). Sin payload: la Cloud Function toma
   * el tenant de `request.auth.token`. Resuelve incluso cuando el sync falla
   * (`status: 'error'`).
   */
  syncTenantNewsNow(): Promise<NewsSourceSyncResult> {
    const fn = httpsCallable<void, NewsSourceSyncResult>(
      this.functions,
      'syncTenantNewsNow',
    );
    return fn().then((res) => res.data);
  }

  /**
   * Dominios autorizados del widget embebible del tenant activo (feature 513,
   * R96). Sin payload: la Cloud Function toma el tenant de
   * `request.auth.token`.
   */
  getEmbedSettings(): Promise<EmbedSettingsView> {
    const fn = httpsCallable<void, EmbedSettingsView>(
      this.functions,
      'getEmbedSettings',
    );
    return fn().then((res) => res.data);
  }

  /**
   * Guarda la lista de dominios autorizados del widget (feature 513, R97). Si
   * alguna entrada no vale, la callable rechaza con `invalid-argument` y
   * `details.rejected` (`EmbedDomainRejected[]`) y no guarda nada (R98).
   */
  saveEmbedAllowedDomains(domains: string[]): Promise<EmbedSettingsView> {
    const fn = httpsCallable<SaveEmbedAllowedDomainsRequest, EmbedSettingsView>(
      this.functions,
      'saveEmbedAllowedDomains',
    );
    return fn({ domains }).then((res) => res.data);
  }

  /** Prueba de conexión con el origen externo de noticias (R10). */
  testNewsSourceConnection(
    payload: TestNewsSourceConnectionRequest,
  ): Promise<TestNewsSourceConnectionResult> {
    const fn = httpsCallable<
      TestNewsSourceConnectionRequest,
      TestNewsSourceConnectionResult
    >(this.functions, 'testNewsSourceConnection');
    return fn(payload).then((res) => res.data);
  }

  /** Send a wizard-URL invitation email to the tenant admin. */
  createInvitation(
    tenantId: string,
    adminEmail: string,
  ): Promise<{ token: string; wizardUrl: string }> {
    const fn = httpsCallable<
      { tenantId: string; adminEmail: string },
      { token: string; wizardUrl: string }
    >(this.functions, 'createTenantInvitation');
    return fn({ tenantId, adminEmail }).then((res) => res.data);
  }

  /** Create a Stripe Checkout session for upgrading a tenant's plan. */
  createPlanUpgradeCheckout(payload: {
    newPlan: string;
    additionalModules: string[];
    successUrl: string;
    cancelUrl: string;
  }): Promise<{ url: string }> {
    const fn = httpsCallable<typeof payload, { url: string }>(
      this.functions,
      'createPlanUpgradeCheckout',
    );
    return fn(payload).then((res) => res.data);
  }

  setTenantCustomDomain(
    tenantId: string,
    hostname: string,
  ): Promise<{ success: boolean }> {
    const fn = httpsCallable<
      { tenantId: string; hostname: string },
      { success: boolean }
    >(this.functions, 'setTenantCustomDomain');
    return fn({ tenantId, hostname }).then((res) => res.data);
  }

  removeTenantCustomDomain(
    tenantId: string,
    hostname: string,
  ): Promise<{ success: boolean }> {
    const fn = httpsCallable<
      { tenantId: string; hostname: string },
      { success: boolean }
    >(this.functions, 'removeTenantCustomDomain');
    return fn({ tenantId, hostname }).then((res) => res.data);
  }
}
